// ============================================================================
// GoalsSection — per-goal progress (animated ring) with baseline / now /
// target and a projected completion date. Renders only active goals; each goal
// card shows only the fields the goal actually has (optional-first).
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components";
import { ProgressArc } from "../charts/ProgressArc";
import { ReportSection } from "./ReportSection";
import type { GoalItem, TypedClientReport } from "../types";
import { clamp, fmtDate, fmtNum } from "../format";

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-kicker text-body-faint">
        {label}
      </p>
      <p className="nums mt-0.5 font-display text-lg font-extrabold tracking-tightest text-body">
        {value}
      </p>
    </div>
  );
}

function GoalCard({ goal }: { goal: GoalItem }) {
  const { t, i18n } = useTranslation("clientReport");
  const locale = i18n.language;
  const pct = goal.progress_pct ?? 0;
  const achieved = pct >= 100;

  // Friendly goal label: prefer the goal's own kind, else map the metric key,
  // else a generic fallback. `i18n.exists` keeps this type-safe.
  let label: string;
  if (goal.kind && goal.kind.trim()) {
    label = goal.kind.charAt(0).toUpperCase() + goal.kind.slice(1);
  } else {
    const metricKeyPath = `goals.metrics.${goal.metric_key}`;
    label = i18n.exists(metricKeyPath, { ns: "clientReport" })
      ? t(metricKeyPath)
      : t("goals.metricFallback");
  }

  return (
    <div className="rounded-lg bg-surface-soft p-5 ring-1 ring-line-soft">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-base font-bold text-body">{label}</h3>
        {achieved && (
          <Badge tone="success" solid>
            {t("goals.achieved")}
          </Badge>
        )}
      </div>

      <div className="mt-4 flex items-center gap-5">
        {goal.progress_pct !== null && (
          <ProgressArc
            pct={clamp(pct, 0, 100)}
            size={104}
            stroke={10}
            accent={achieved ? "lime" : "coral"}
            ariaLabel={`${label} ${Math.round(pct)}%`}
          >
            <span className="nums font-display text-2xl font-extrabold tracking-tightest text-body">
              {fmtNum(Math.round(clamp(pct, 0, 100)), 0, locale)}
              <span className="text-sm">%</span>
            </span>
          </ProgressArc>
        )}

        <div className="grid flex-1 grid-cols-2 gap-x-4 gap-y-3">
          {goal.baseline !== null && (
            <Stat label={t("goals.baseline")} value={fmtNum(goal.baseline, 1, locale)} />
          )}
          {goal.current !== null && (
            <Stat label={t("goals.current")} value={fmtNum(goal.current, 1, locale)} />
          )}
          {goal.target !== null && (
            <Stat label={t("goals.target")} value={fmtNum(goal.target, 1, locale)} />
          )}
          {goal.target_date && (
            <Stat label={t("goals.targetDate")} value={fmtDate(goal.target_date, locale)} />
          )}
        </div>
      </div>

      {goal.projection && goal.projection.date && !achieved && (
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-md bg-ocean-soft px-3 py-2 text-sm">
          <span className="font-bold text-ocean-600">{t("goals.projection")}</span>
          <span className="font-display font-extrabold text-body">
            {fmtDate(goal.projection.date, locale)}
          </span>
          <span className="text-body-soft">
            (
            {t("goals.projectionWeeks", {
              weeks: fmtNum(goal.projection.weeks, 0, locale),
            })}
            )
          </span>
        </div>
      )}
    </div>
  );
}

export function GoalsSection({
  report,
  index,
}: {
  report: TypedClientReport;
  index: number;
}) {
  const { t } = useTranslation("clientReport");
  const { goals } = report;

  return (
    <ReportSection
      id="report-goals"
      index={index}
      kicker={t("kicker")}
      title={t("goals.title")}
      subtitle={t("goals.subtitle")}
      hasData={goals.ok}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {goals.items.map((goal, i) => (
          <GoalCard key={`${goal.metric_key}-${i}`} goal={goal} />
        ))}
      </div>
    </ReportSection>
  );
}
